// EditorMap is the panel on the screen that contains the map. It is
// responsible for the full panel as well as the map grid and all tiles
// within.

import { useMapStoreContext } from 'app/src/editor/MapStoreContext';
import MapTile from 'app/src/editor/MapTile';
import { usePluginsContext } from 'app/src/editor/PluginsContext';
import { useTileHoverContext } from 'app/src/editor/TileHoverContext';
import { useUndoRedoContext } from 'app/src/editor/UndoRedoContext';
import { MovableItemOrientation } from 'app/src/engine/movable-items/MovableItem';
import * as React from 'react';

export default function EditorMap(): React.ReactElement {
	const { mapStore } = useMapStoreContext();
	const { setTileCoordinates } = useTileHoverContext();
	const { addUndoState } = useUndoRedoContext();
	const plugins = usePluginsContext();

	return (
		<div className="EditorMap">
			<div className="EditormapContainer">
				<div className="EditorMap-topControls">
					<button
						className="EditorMap-mapResizeControl EditorMap-mapResizeControl--addRowAtTop"
						onClick={(): void => {
							mapStore.addRowAtTop();
							addUndoState();
						}}
					/>
					<button
						className="EditorMap-mapResizeControl EditorMap-mapResizeControl--removeTopRow"
						onClick={(): void => {
							mapStore.removeTopRow();
							addUndoState();
						}}
					/>
				</div>
				<div className="EditorMap-main">
					<div className="EditorMap-leftControls">
						<button
							className="EditorMap-mapResizeControl EditorMap-mapResizeControl--addColumnAtLeft"
							onClick={(): void => {
								mapStore.addColumnAtLeft();
								addUndoState();
							}}
						/>
						<button
							className="EditorMap-mapResizeControl EditorMap-mapResizeControl--removeLeftColumn"
							onClick={(): void => {
								mapStore.removeLeftColumn();
								addUndoState();
							}}
						/>
					</div>
					<table
						className="EditorMap-grid"
						onMouseLeave={(): void => {
							setTileCoordinates(null);
						}}
					>
						<tbody>
							{mapStore.tiles.map((rowArr, row) => {
								return (
									<tr key={`EditorMap--row-${row}`}>
										{rowArr.map((tile, col) => {
											let containsStar = false;
											for (const star of mapStore.stars) {
												if (star.row === row && star.col === col) {
													containsStar = true;
													break;
												}
											}

											let robotOrientation: MovableItemOrientation | null = null;
											if (mapStore.robot) {
												if (mapStore.robot.row === row && mapStore.robot.col === col) {
													robotOrientation = mapStore.robot.orientation;
												}
											}

											const plugin = Array.from(plugins).find((p) => p.providesNodeForTile(tile));
											const tileNode = (plugin && plugin.getTileNode(tile, row, col)) || null;

											return (
												<td key={`EditorMap--row-${row}-col-${col}`} className="EditorMap-cell">
													<MapTile
														row={row}
														col={col}
														tile={tile}
														containsStar={containsStar}
														robotOrientation={robotOrientation}
													>
														{tileNode}
													</MapTile>
												</td>
											);
										})}
									</tr>
								);
							})}
						</tbody>
					</table>
					<div className="EditorMap-rightControls">
						<button
							className="EditorMap-mapResizeControl EditorMap-mapResizeControl--addColumnAtRight"
							onClick={(): void => {
								mapStore.addColumnAtRight();
								addUndoState();
							}}
						/>
						<button
							className="EditorMap-mapResizeControl EditorMap-mapResizeControl--removeRightColumn"
							onClick={(): void => {
								mapStore.removeRightColumn();
								addUndoState();
							}}
						/>
					</div>
				</div>
				<div className="EditorMap-bottomControls">
					<button
						className="EditorMap-mapResizeControl EditorMap-mapResizeControl--addRowAtBottom"
						onClick={(): void => {
							mapStore.addRowAtBottom();
							addUndoState();
						}}
					/>
					<button
						className="EditorMap-mapResizeControl EditorMap-mapResizeControl--removeBottomRow"
						onClick={(): void => {
							mapStore.removeBottomRow();
							addUndoState();
						}}
					/>
				</div>
			</div>
		</div>
	);
}
